import { Global } from '@utils/common';
import { ID } from './ID';
import { CustomColumnType } from './CustomColumnItem';
import { QualQuestion } from './QualQuestion';
import { AnswerOption } from './SupplierQualAnswer';

export const getColumnHeaderDataTypes = () => ({
  NUMERIC: Global.lots.custom_column_headers.data_types.numeric,
  TEXT: Global.lots.custom_column_headers.data_types.text
} as const);

export type CustomColumnHeaderDataType = ReturnType<typeof getColumnHeaderDataTypes>[
  keyof ReturnType<typeof getColumnHeaderDataTypes>
];

export const getDataTypes = (): CustomColumnHeaderDataType[] =>
  Object.values(getColumnHeaderDataTypes());

export const getTextDataType = (): CustomColumnHeaderDataType =>
  getColumnHeaderDataTypes().TEXT;

export const getNumericDataType = (): CustomColumnHeaderDataType =>
  getColumnHeaderDataTypes().NUMERIC;

/** Taxonomy key served by GET /api/v1/adder_taxonomy_options — no longer hardcoded. */
export type TaxonomyKey = string;

export interface AdderColumnConfig {
  is_required: boolean; // always true in Increment 1
  lot_ids?: number[] | null; // null = all lots, [1, 3] = specific lots
  multiplier_operand_source?: 'extended_price' | 'price_per_unit' | 'quantity_of_units' | null; // set only when scope is custom_multiplier
  rate_type?: 'percentage' | 'price' | null; // set only when scope is custom_multiplier
  scope: 'custom_multiplier' | 'per_line' | 'per_unit';
  taxonomy: TaxonomyKey | null;
}

export interface CustomColumnHeader {
  _adding?: boolean;
  _destroy?: boolean;
  adder_column_config?: AdderColumnConfig | null;
  answer_option?: AnswerOption;
  character_limit: number;
  column_order_index: number;
  column_type: CustomColumnType;
  created_at: string;
  data_type: CustomColumnHeaderDataType;
  display_text: string | null;
  id: ID;
  is_supplier_visible: boolean;
  lot_id: ID;
  qual_question?: QualQuestion | null;
  qualifying_equality: string;
  /** Resolved tooltip text attached only for the quote-spreadsheet download payload — mirrors the
   * in-app tooltip exactly (see getAdderColumnTooltipParams / getQualQuestionTooltipParams). Never
   * persisted or read back from the API. */
  tooltip_text?: string;
  updated_at?: string;
}

export interface CustomColumnHeaderToClone extends Omit<CustomColumnHeader, '_adding' | '_destroy'> {
  answer_option_id?: ID | null;
  qual_question_id?: ID;
}

export interface AdderCustomColumnHeader extends CustomColumnHeader {
  adder_column_config: AdderColumnConfig;
}

export interface AdderValue {
  custom_column_header_id: number;
  supplier_provided_rate_value?: number | null; // percentage/rate input
  supplier_provided_value: number | null; // quoting-currency contribution
  value: number; // event-currency contribution
}

/** Payload shape for submitting adder column values to the API. */
export interface AdderColumnValuePayload {
  custom_column_header_id: ID;
  value: number;
}
